import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "../components/Layout";
import { api } from "../api/client";
import type {
  DashboardDueSoonItem,
  DashboardLeadFollowUpItem,
  DashboardLowInventoryItem,
  DashboardRecentIncidentItem,
  DashboardWorkOrderWeekItem,
  WorkOrderListItem,
} from "../api/types";
import {
  formatDate,
  formatDateTime,
  formatWoId,
  isOverdue,
  woPriorityLabel,
  woStatusLabel,
} from "../lib/workorders";
import { formatQuantity } from "../lib/inventory";
import { isLeadOverdue, leadStatusBadge, leadStatusLabel } from "../lib/leads";
import {
  formatIncidentId,
  incidentTypeLabel,
  severityBadge,
  severityLabel,
} from "../lib/incidents";

export function DashboardPage() {
  const summary = useQuery({
    queryKey: ["dashboard", "summary"],
    queryFn: () => api.dashboardSummary(),
  });
  const dueSoon = useQuery({
    queryKey: ["dashboard", "due-soon"],
    queryFn: () => api.dashboard.dueSoon(),
  });
  const workOrderWeek = useQuery({
    queryKey: ["dashboard", "work-order-week"],
    queryFn: () => api.dashboard.workOrderWeek(),
  });
  const workOrders = useQuery({
    queryKey: ["work-orders", "dashboard-calendar-demo"],
    queryFn: () => api.workOrders.list({}),
  });
  const lowInventory = useQuery({
    queryKey: ["dashboard", "low-inventory"],
    queryFn: () => api.dashboard.lowInventory(),
  });
  const leadFollowUps = useQuery({
    queryKey: ["dashboard", "lead-follow-ups"],
    queryFn: () => api.dashboard.leadFollowUps(),
  });
  const recentIncidents = useQuery({
    queryKey: ["dashboard", "recent-incidents"],
    queryFn: () => api.dashboard.recentIncidents(),
  });

  const counts = summary.data?.counts;
  const calendarStartDate = workOrderWeek.data?.startDate ?? todayKey();
  const liveCalendarItems = workOrderWeek.data?.items ?? [];
  const demoCalendarItems = liveCalendarItems.length
    ? liveCalendarItems
    : demoWeekItems(workOrders.data?.workOrders ?? [], calendarStartDate);
  const usingDemoPlacement = liveCalendarItems.length === 0 && demoCalendarItems.length > 0;

  return (
    <Layout title="Dashboard">
      <p className="help-line">
        Your daily summary across <strong>all your buildings</strong>. Tap any counter or item to
        open the matching list.
      </p>

      <div className="stat-row">
        <Link
          to="/work-orders?due=overdue"
          className="stat stat-alert"
          aria-label="Overdue work orders — open the list"
        >
          <span className="stat-num">
            {statValue(counts?.overdueWorkOrders, summary.isLoading, summary.isError)}
          </span>
          <span className="stat-label">Overdue work orders</span>
        </Link>
        <Link to="/work-orders" className="stat" aria-label="Open work orders — open the list">
          <span className="stat-num">
            {statValue(counts?.openWorkOrders, summary.isLoading, summary.isError)}
          </span>
          <span className="stat-label">Open work orders</span>
        </Link>
        <Link to="/leads" className="stat" aria-label="Lead follow-ups — open the list">
          <span className="stat-num">
            {statValue(counts?.leadFollowUps, summary.isLoading, summary.isError)}
          </span>
          <span className="stat-label">Lead follow-ups</span>
        </Link>
      </div>

      <section className="card dashboard-calendar-card">
        <h2>Work order calendar view - example 1</h2>
        <p className="sub">
          Maintenance workers as rows, days as columns.
          {usingDemoPlacement
            ? " Using current work orders placed across this week for preview."
            : ""}
        </p>
        <PanelState
          isLoading={workOrderWeek.isLoading || workOrders.isLoading}
          isError={workOrderWeek.isError || workOrders.isError}
          isEmpty={!demoCalendarItems.length}
          emptyText="No scheduled work orders in the next seven days."
        >
          <WorkerGridCalendar
            items={demoCalendarItems}
            startDate={calendarStartDate}
          />
        </PanelState>
      </section>

      <section className="card dashboard-calendar-card">
        <h2>Work order calendar view - example 2</h2>
        <p className="sub">
          Day-by-day agenda with each worker nested under the day.
          {usingDemoPlacement
            ? " Using current work orders placed across this week for preview."
            : ""}
        </p>
        <PanelState
          isLoading={workOrderWeek.isLoading || workOrders.isLoading}
          isError={workOrderWeek.isError || workOrders.isError}
          isEmpty={!demoCalendarItems.length}
          emptyText="No scheduled work orders in the next seven days."
        >
          <DayAgendaCalendar
            items={demoCalendarItems}
            startDate={calendarStartDate}
          />
        </PanelState>
      </section>

      <div className="two-col">
        <section className="card">
          <h2>Due soon</h2>
          <p className="sub">Next work orders by due date — overdue at the top.</p>
          <PanelState
            isLoading={dueSoon.isLoading}
            isError={dueSoon.isError}
            isEmpty={!dueSoon.data?.items.length}
            emptyText="Nothing due — you're caught up."
          >
            <ul className="panel-list">
              {dueSoon.data?.items.map((it) => (
                <li key={it.id}>
                  <DueSoonRow item={it} />
                </li>
              ))}
            </ul>
          </PanelState>
        </section>

        <section className="card">
          <h2>Lead follow-ups</h2>
          <p className="sub">Leads with a follow-up due today or earlier.</p>
          <PanelState
            isLoading={leadFollowUps.isLoading}
            isError={leadFollowUps.isError}
            isEmpty={!leadFollowUps.data?.items.length}
            emptyText="No follow-ups due."
          >
            <ul className="panel-list">
              {leadFollowUps.data?.items.map((it) => (
                <li key={it.id}>
                  <LeadFollowUpRow item={it} />
                </li>
              ))}
            </ul>
          </PanelState>
        </section>
      </div>

      <div className="two-col">
        <section className="card">
          <h2>Recent incidents</h2>
          <p className="sub">Five most recent incidents across your buildings.</p>
          <PanelState
            isLoading={recentIncidents.isLoading}
            isError={recentIncidents.isError}
            isEmpty={!recentIncidents.data?.items.length}
            emptyText="No incidents recorded."
          >
            <ul className="panel-list">
              {recentIncidents.data?.items.map((it) => (
                <li key={it.id}>
                  <IncidentRow item={it} />
                </li>
              ))}
            </ul>
          </PanelState>
        </section>

        <section className="card">
          <h2>Low inventory</h2>
          <p className="sub">Active items at or below their reorder threshold.</p>
          <PanelState
            isLoading={lowInventory.isLoading}
            isError={lowInventory.isError}
            isEmpty={!lowInventory.data?.items.length}
            emptyText="All stocked above thresholds."
          >
            <ul className="panel-list">
              {lowInventory.data?.items.map((it) => (
                <li key={it.id}>
                  <LowInventoryRow item={it} />
                </li>
              ))}
            </ul>
          </PanelState>
        </section>
      </div>
    </Layout>
  );
}

function statValue(
  v: number | undefined,
  isLoading: boolean,
  isError: boolean,
): string | number {
  if (isLoading) return "…";
  if (isError) return "—";
  return v ?? 0;
}

// ---- Panel state wrapper --------------------------------------------------

function PanelState({
  isLoading,
  isError,
  isEmpty,
  emptyText,
  children,
}: {
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  emptyText: string;
  children: React.ReactNode;
}) {
  if (isLoading) return <p className="panel-empty">Loading…</p>;
  if (isError) return <p className="panel-empty">Couldn't load this panel.</p>;
  if (isEmpty) return <p className="panel-empty">{emptyText}</p>;
  return <>{children}</>;
}

// ---- Work order calendar examples ----------------------------------------

function WorkerGridCalendar({
  items,
  startDate,
}: {
  items: DashboardWorkOrderWeekItem[];
  startDate: string;
}) {
  const days = weekDays(startDate);
  const workers = workerGroups(items);
  const [selectedId, setSelectedId] = useState(items[0]?.id ?? 0);
  const [modalSelectedId, setModalSelectedId] = useState<number | null>(null);
  const [collapsedWorkers, setCollapsedWorkers] = useState<Set<string>>(() => new Set());
  const modalSelected = items.find((item) => item.id === modalSelectedId);

  function selectItem(id: number) {
    setSelectedId(id);
    setModalSelectedId(id);
  }

  function toggleWorker(key: string) {
    setCollapsedWorkers((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div className="wo-calendar-demo">
      <div className="worker-grid-calendar" style={{ gridTemplateColumns: `150px repeat(${days.length}, minmax(110px, 1fr))` }}>
        <div className="worker-grid-corner">Maintenance</div>
        {days.map((day) => (
          <div key={day.key} className="worker-grid-day">
            <strong>{day.weekday}</strong>
            <span>{day.label}</span>
          </div>
        ))}
        {workers.map((worker) => (
          <WorkerGridRow
            key={worker.key}
            worker={worker}
            days={days}
            items={items}
            selectedId={selectedId}
            onSelect={selectItem}
            collapsed={collapsedWorkers.has(worker.key)}
            onToggle={() => toggleWorker(worker.key)}
          />
        ))}
      </div>
      <WorkOrderQuickViewModal item={modalSelected} onClose={() => setModalSelectedId(null)} />
    </div>
  );
}

function WorkerGridRow({
  worker,
  days,
  items,
  selectedId,
  onSelect,
  collapsed,
  onToggle,
}: {
  worker: WorkerGroup;
  days: WeekDay[];
  items: DashboardWorkOrderWeekItem[];
  selectedId: number;
  onSelect: (id: number) => void;
  collapsed: boolean;
  onToggle: () => void;
}) {
  const workerItems = items.filter((item) => workerKey(item) === worker.key);
  if (collapsed) {
    return (
      <button
        className="worker-grid-collapsed-row"
        style={{ gridColumn: "1 / -1" }}
        onClick={onToggle}
        aria-expanded="false"
        aria-label={`Expand ${worker.name}'s week`}
      >
        <span className="worker-grid-arrow collapsed" aria-hidden="true" />
        <strong>{worker.name}</strong>
      </button>
    );
  }

  return (
    <>
      <div className="worker-grid-name">
        <button
          className="worker-grid-toggle"
          onClick={onToggle}
          aria-expanded="true"
          aria-label={`Collapse ${worker.name}'s week`}
        >
          <span className="worker-grid-arrow expanded" aria-hidden="true" />
          <strong>{worker.name}</strong>
          <span>{workerItems.length} work orders</span>
        </button>
      </div>
      {days.map((day) => {
        const dayItems = workerItems.filter((item) => item.scheduledDate === day.key);
        return (
          <div key={`${worker.key}-${day.key}`} className="worker-grid-cell">
            {dayItems.length === 0 ? (
              <span className="calendar-empty-slot">-</span>
            ) : (
              dayItems.map((item) => (
                <button
                  key={item.id}
                  className={"wo-calendar-pill" + (item.id === selectedId ? " selected" : "")}
                  onClick={() => onSelect(item.id)}
                  aria-haspopup="dialog"
                >
                  <strong>{formatWoId(item.id)}</strong>
                  <span>{item.title}</span>
                </button>
              ))
            )}
          </div>
        );
      })}
    </>
  );
}

function DayAgendaCalendar({
  items,
  startDate,
}: {
  items: DashboardWorkOrderWeekItem[];
  startDate: string;
}) {
  const days = weekDays(startDate);
  const [selectedId, setSelectedId] = useState(items[0]?.id ?? 0);
  const [modalSelectedId, setModalSelectedId] = useState<number | null>(null);
  const modalSelected = items.find((item) => item.id === modalSelectedId);

  function selectItem(id: number) {
    setSelectedId(id);
    setModalSelectedId(id);
  }

  return (
    <div className="wo-calendar-demo">
      <div className="day-agenda-calendar">
        {days.map((day) => {
          const dayItems = items.filter((item) => item.scheduledDate === day.key);
          const workers = workerGroups(dayItems);
          return (
            <div key={day.key} className="agenda-day">
              <div className="agenda-day-head">
                <strong>{day.weekday}</strong>
                <span>{day.label}</span>
              </div>
              {dayItems.length === 0 ? (
                <p className="calendar-empty-slot">No work orders</p>
              ) : (
                workers.map((worker) => (
                  <div key={`${day.key}-${worker.key}`} className="agenda-worker">
                    <h3 className="agenda-worker-heading">
                      <WorkerAvatar name={worker.name} />
                      <span>{worker.name}</span>
                    </h3>
                    {dayItems
                      .filter((item) => workerKey(item) === worker.key)
                      .map((item) => (
                        <button
                          key={item.id}
                          className={"agenda-work-order" + (item.id === selectedId ? " selected" : "")}
                          onClick={() => selectItem(item.id)}
                          aria-haspopup="dialog"
                        >
                          <span className="agenda-work-order-id">{formatWoId(item.id)}</span>
                          <span>{item.title}</span>
                          <span className={`badge badge-${statusBadge(item.status)}`}>
                            {woStatusLabel(item.status)}
                          </span>
                        </button>
                      ))}
                  </div>
                ))
              )}
            </div>
          );
        })}
      </div>
      <WorkOrderQuickViewModal item={modalSelected} onClose={() => setModalSelectedId(null)} />
    </div>
  );
}

function WorkerAvatar({ name }: { name: string }) {
  const avatar = workerAvatar(name);
  if (!avatar) {
    return (
      <span className="agenda-worker-avatar agenda-worker-avatar--fallback" aria-hidden="true">
        {workerInitials(name)}
      </span>
    );
  }

  return (
    <img
      className="agenda-worker-avatar"
      src={avatar.src}
      alt=""
      title={avatar.title}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={(event) => {
        event.currentTarget.style.display = "none";
      }}
    />
  );
}

function WorkOrderQuickViewContent({
  item,
  titleId,
}: {
  item: DashboardWorkOrderWeekItem;
  titleId?: string;
}) {
  return (
    <>
      <div>
        <span className="eyebrow">Quick view</span>
        <h3 id={titleId}>
          {formatWoId(item.id)} - {item.title}
        </h3>
      </div>
      <dl className="detail-list">
        <div>
          <dt>Maintenance</dt>
          <dd>{item.assignedName}</dd>
        </div>
        <div>
          <dt>Scheduled</dt>
          <dd>{formatDate(item.scheduledDate)}</dd>
        </div>
        <div>
          <dt>Due</dt>
          <dd>{formatDate(item.dueDate)}</dd>
        </div>
        <div>
          <dt>Location</dt>
          <dd>
            {item.buildingName}
            {item.where !== "-" ? ` - ${item.where}` : ""}
          </dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>
            <span className={`badge badge-${statusBadge(item.status)}`}>
              {woStatusLabel(item.status)}
            </span>
          </dd>
        </div>
        <div>
          <dt>Priority</dt>
          <dd>{woPriorityLabel(item.priority)}</dd>
        </div>
      </dl>
      <Link to={`/work-orders/${item.id}`} className="btn btn-primary btn-xl">
        Open work order
      </Link>
    </>
  );
}

function WorkOrderQuickViewModal({
  item,
  onClose,
}: {
  item: DashboardWorkOrderWeekItem | undefined;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!item) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [item, onClose]);

  if (!item) return null;

  return (
    <div className="maintenance-modal-backdrop" role="presentation" onClick={onClose}>
      <section
        className="wo-quick-view maintenance-calendar-modal dashboard-calendar-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dashboard-calendar-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button type="button" className="maintenance-modal-close" onClick={onClose} aria-label="Close quick view">
          X
        </button>
        <WorkOrderQuickViewContent item={item} titleId="dashboard-calendar-modal-title" />
      </section>
    </div>
  );
}

type WeekDay = { key: string; weekday: string; label: string };
type WorkerGroup = { key: string; name: string };
type WorkerAvatarInfo = { src: string; title: string };

const WORKER_AVATARS: Record<string, WorkerAvatarInfo> = {
  "b.j. hunnicutt": {
    src: commonsImage("MASH Loretta Swit Mike Farrell 1976.JPG"),
    title: "B.J. Hunnicutt",
  },
  "hawkeye pierce": {
    src: commonsImage("Alan Alda Hawkeye MASH.JPG"),
    title: "Hawkeye Pierce",
  },
  "max klinger": {
    src: commonsImage("Jamie Farr Max Klinger MASH 1975.JPG"),
    title: "Max Klinger",
  },
  "maxwell klinger": {
    src: commonsImage("Jamie Farr Max Klinger MASH 1975.JPG"),
    title: "Max Klinger",
  },
  "margaret houlihan": {
    src: commonsImage("Loretta Swit Hot Lips MASH 1972.JPG"),
    title: "Margaret Houlihan",
  },
  "radar o'reilly": {
    src: commonsImage("Gary Burghoff Radar MASH 1975.JPG"),
    title: "Radar O'Reilly",
  },
  "walter o'reilly": {
    src: commonsImage("Gary Burghoff Radar MASH 1975.JPG"),
    title: "Radar O'Reilly",
  },
  "charles winchester": {
    src: commonsImage("David Ogden Stiers in MASH.jpg"),
    title: "Charles Winchester",
  },
  "charles emerson winchester": {
    src: commonsImage("David Ogden Stiers in MASH.jpg"),
    title: "Charles Winchester",
  },
  "frank burns": {
    src: commonsImage("Larry Linville Major Frank Burns MASH 1972.JPG"),
    title: "Frank Burns",
  },
};

function commonsImage(fileName: string): string {
  return `https://commons.wikimedia.org/wiki/Special:FilePath/${encodeURIComponent(fileName)}?width=96`;
}

function workerAvatar(name: string): WorkerAvatarInfo | undefined {
  return WORKER_AVATARS[name.trim().toLowerCase()];
}

function workerInitials(name: string): string {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
  return initials || "?";
}

function weekDays(startDate: string): WeekDay[] {
  if (!startDate) return [];
  const start = new Date(`${startDate}T00:00:00`);
  return Array.from({ length: 7 }).map((_, index) => {
    const d = new Date(start);
    d.setDate(start.getDate() + index);
    return {
      key: d.toISOString().slice(0, 10),
      weekday: d.toLocaleDateString("en-US", { weekday: "short" }),
      label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    };
  });
}

function workerGroups(items: DashboardWorkOrderWeekItem[]): WorkerGroup[] {
  const groups = new Map<string, WorkerGroup>();
  for (const item of items) {
    const key = workerKey(item);
    if (!groups.has(key)) groups.set(key, { key, name: item.assignedName });
  }
  return Array.from(groups.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function workerKey(item: DashboardWorkOrderWeekItem): string {
  return `${item.assignedMaintenanceId ?? "unassigned"}:${item.assignedName}`;
}

function statusBadge(status: string): string {
  if (status === "completed") return "ok";
  if (status === "on_hold") return "warn";
  if (status === "cancelled") return "muted";
  if (status === "in_progress") return "info";
  return "info";
}

function demoWeekItems(
  workOrders: WorkOrderListItem[],
  startDate: string,
): DashboardWorkOrderWeekItem[] {
  const days = weekDays(startDate);
  if (!days.length) return [];
  return workOrders
    .filter((item) => item.status !== "completed" && item.status !== "cancelled")
    .slice(0, 14)
    .map((item, index) => ({
      id: item.id,
      title: item.title,
      status: item.status,
      priority: item.priority,
      dueDate: item.dueDate,
      assignedDate: item.assignedDate,
      scheduledDate: item.assignedDate ?? item.dueDate ?? days[index % days.length].key,
      buildingName: item.buildingName,
      where: item.where,
      assignedMaintenanceId: item.assignedMaintenanceId,
      assignedName: item.assignedName ?? "Unassigned",
    }))
    .map((item, index) => ({
      ...item,
      scheduledDate: days[index % days.length].key,
    }));
}

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

// ---- Row components -------------------------------------------------------

function DueSoonRow({ item }: { item: DashboardDueSoonItem }) {
  const overdue = isOverdue(item.status, item.dueDate);
  return (
    <Link
      to={`/work-orders/${item.id}`}
      className="panel-row"
      aria-label={`${formatWoId(item.id)} — open work order`}
    >
      <div>
        <div className="primary">
          {formatWoId(item.id)} · {item.title}
        </div>
        <div className="meta">
          {item.buildingName}
          {item.where !== "—" ? ` · ${item.where}` : ""}
          {item.assignedName ? ` · ${item.assignedName}` : " · Unassigned"}
        </div>
      </div>
      <div className="right">
        <span className={overdue ? "overdue" : ""}>
          {overdue ? "Overdue · " : ""}
          {formatDate(item.dueDate)}
        </span>
        <div className="meta">{woStatusLabel(item.status)}</div>
      </div>
    </Link>
  );
}

function LeadFollowUpRow({ item }: { item: DashboardLeadFollowUpItem }) {
  const overdue = isLeadOverdue(item.status, item.followUpDate);
  return (
    <Link
      to={`/leads/${item.id}`}
      className="panel-row"
      aria-label={`${item.contactName} — open lead`}
    >
      <div>
        <div className="primary">{item.contactName}</div>
        <div className="meta">
          {item.buildingName}
          {item.phone ? ` · ${item.phone}` : ""}
        </div>
      </div>
      <div className="right">
        <span className={`badge badge-${leadStatusBadge(item.status)}`}>
          {leadStatusLabel(item.status)}
        </span>
        <div className={"meta " + (overdue ? "overdue" : "")}>
          {overdue ? "Overdue · " : "Due "}
          {formatDate(item.followUpDate)}
        </div>
      </div>
    </Link>
  );
}

function IncidentRow({ item }: { item: DashboardRecentIncidentItem }) {
  return (
    <Link
      to={`/incidents/${item.id}`}
      className="panel-row"
      aria-label={`${formatIncidentId(item.id)} — open incident`}
    >
      <div>
        <div className="primary">
          {formatIncidentId(item.id)} · {incidentTypeLabel(item.incidentType)}
        </div>
        <div className="meta">
          {item.buildingName}
          {item.where !== "—" ? ` · ${item.where}` : ""}
          {item.tenantName ? ` · ${item.tenantName}` : ""}
        </div>
      </div>
      <div className="right">
        <span className={`badge badge-${severityBadge(item.severity)}`}>
          {severityLabel(item.severity)}
        </span>
        <div className="meta">{formatDateTime(item.incidentDatetime)}</div>
      </div>
    </Link>
  );
}

function LowInventoryRow({ item }: { item: DashboardLowInventoryItem }) {
  return (
    <Link
      to={`/inventory/${item.id}`}
      className="panel-row"
      aria-label={`${item.name} — open inventory item`}
    >
      <div>
        <div className="primary">{item.name}</div>
        <div className="meta">{item.buildingName}</div>
      </div>
      <div className="right">
        <span className="badge badge-warn">
          {formatQuantity(item.quantity, item.unitOfMeasure)}
        </span>
        <div className="meta">
          Reorder at {formatQuantity(item.reorderThreshold ?? 0, item.unitOfMeasure)}
        </div>
      </div>
    </Link>
  );
}
